import { useObservableState } from 'observable-hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { map } from 'rxjs/operators';
import Form, { SubmitType } from '../../../components/form';
import Button from '../../../components/form/elements/button';
import Checkbox from '../../../components/form/elements/checkbox';
import Group from '../../../components/form/elements/group';
import Link from '../../../components/form/elements/link';
import Decimal from '../../../components/form/elements/decimal';
import Readonly from '../../../components/form/elements/readonly';
import Select from '../../../components/form/elements/select';
import Show from '../../../components/form/elements/show';
import Switch from '../../../components/form/elements/switch';
import Text from '../../../components/form/elements/text';
import TextArea from '../../../components/form/elements/text-area';
import Watch from '../../../components/form/elements/watch';
import Classifier from '../../../enums/classifier';
import ClassifierGroup from '../../../enums/classifier-group';
import ClassifierValue from '../../../interfaces/classifierValue';
import Paginate from '../../../interfaces/paginate';
import { Product } from '../../../interfaces/product';
import { WAREHOUSE_PRODUCTS_VIEW } from '../../../router/routes';
import { useService } from '../../../services/services';
import { route } from '../../../services/utils';
import { productSchema } from './view';
import { ProductObtainType } from '../../../enums/product-obtain-type';
import ClassifierPropertyKey from '../../../enums/classifier-property-key';

export default function ProductsEdit() {
  const { t } = useTranslation();
  const history = useHistory();
  const { farmId, obtainTypeId, id } = useParams<{
    farmId: string;
    obtainTypeId: ProductObtainType;
    id: string;
  }>();
  const { productService, classifierService } = useService();
  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements, setFooterElements] = useState([]);

  const [product] = useObservableState<any>(
    () =>
      productService.show(farmId, id).pipe(
        map((values) => {
          return {
            ...values,
            obtain_type_name: values.obtain_type.name,
            category_id: {
              value: values.category.id,
              label: values.category.value,
            },
            type_id: {
              value: values.type.id,
              label: values.type.value,
              units: Object.values(values.type.properties).find(
                (v) => v.property.key === ClassifierPropertyKey.UNITS_OF_MEASURE
              )?.value,
            },
            name:
              values.category.value === 'Augu aizsardzības līdzekļi' ||
              values.category.value === 'Mēslojums'
                ? {
                    label: values.name,
                  }
                : values.name,
            unit: {
              value: values.unit,
              label: values.unit,
            },
          };
        })
      ),
    null
  );

  useEffect(() => {
    if (product === null) {
      return;
    }

    setBodyElements([
      Group([
        Readonly([
          Text('obtain_type_name', t('pages.warehouse.products.type')),
        ]),
        Watch(
          Select('category_id', {
            asyncOptions: (value, loadedOptions, { page, getValues }) =>
              classifierService
                .valuesPaged(
                  ClassifierGroup.WAREHOUSE,
                  Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                  {
                    page,
                    [obtainTypeId]: true,
                    value,
                  }
                )
                .pipe(
                  map((response: Paginate<ClassifierValue>) => {
                    return {
                      hasMore: response.total === response.per_page,
                      options: response.data.map((v) => ({
                        label: v.value,
                        value: v.id,
                      })),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                ),
            multi: false,
            label: 'pages.warehouse.products.product_category',
          }),
          ({ value, setValue }) => {
            setValue('type_id', null);
          }
        ),
        Watch(
          Select('type_id', {
            asyncOptions: (value, loadedOptions, { page, getValues }) => {
              const params: any = {
                page,
                [obtainTypeId]: true,
                value,
              };

              const categoryId = getValues('category_id')?.value;
              if (categoryId) {
                params['parent_id'] = categoryId;
              }

              return classifierService
                .valuesPaged(
                  ClassifierGroup.WAREHOUSE,
                  Classifier.PRODUCT_TYPES,
                  params
                )
                .pipe(
                  map((response: Paginate<ClassifierValue>) => {
                    return {
                      hasMore: response.total === response.per_page,
                      options: response.data.map((v) => ({
                        label: v.value,
                        value: v.id,
                        units: Object.values(v.properties).find(
                          (v) =>
                            v.property.key ===
                            ClassifierPropertyKey.UNITS_OF_MEASURE
                        )?.value,
                      })),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                );
            },
            cacheOn: ['category_id'],
            multi: false,
            label: 'pages.warehouse.products.product_type',
          }),
          ({ value, setValue, getValues }) => {
            const name = getValues('name');
            if (
              name &&
              typeof name !== 'string' &&
              value !== '' &&
              (value === null || product.type_id?.value !== value.value)
            ) {
              setValue('name', null);
              setValue('unit', null);
            }
          }
        ),
        Switch(
          Select('name', {
            asyncOptions: (value, loadedOptions, { page, getValues }) => {
              const params: any = { page, value };

              const typeValue = getValues('type_id')?.value;
              if (typeValue) {
                params['fertilizer_type'] = typeValue;
              }

              return classifierService
                .valuesPaged(
                  ClassifierGroup.WAREHOUSE,
                  Classifier.FERTILIZERS,
                  params
                )
                .pipe(
                  map((response: Paginate<ClassifierValue>) => {
                    return {
                      hasMore: response.total === response.per_page,
                      options: response.data.map((v) => ({
                        label: v.value,
                        value: v.id,
                      })),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                );
            },
            cacheOn: ['type_id'],
            multi: false,
            label: 'pages.warehouse.products.name',
          }),
          Switch(
            Watch(
              Select('name', {
                asyncOptions: (value, loadedOptions, { page, getValues }) => {
                  const params: any = { page, value };

                  const typeValue = getValues('type_id')?.value;
                  if (typeValue) {
                    params['plant_protection_product_type'] = typeValue;
                  }

                  return classifierService
                    .valuesPaged(
                      ClassifierGroup.WAREHOUSE,
                      Classifier.PLANT_PROTECTION_PRODUCTS,
                      params
                    )
                    .pipe(
                      map((response: Paginate<ClassifierValue>) => {
                        return {
                          hasMore: response.total === response.per_page,
                          options: response.data.map((v) => ({
                            label: v.value,
                            value: v.id,
                          })),
                          additional: { page: response.current_page + 1 },
                        };
                      })
                    );
                },
                cacheOn: ['type_id'],
                multi: false,
                label: 'pages.warehouse.products.name',
              }),
              ({ value, setValue, getValues }) => {
                if (value === null || product.name.value !== value.value) {
                  setValue('unit', null);
                }
              }
            ),
            Text('name', t('pages.warehouse.products.name')),
            'category_id',
            (value) => value?.label === 'Augu aizsardzības līdzekļi'
          ),
          'category_id',
          (value) => value?.label === 'Mēslojums'
        ),
        Select('unit', {
          asyncOptions: async (value, loadedOptions, { page, getValues }) => {
            return {
              hasMore: false,
              options:
                getValues('type_id')?.units?.map((v: string) => ({
                  label: v,
                  value: v,
                })) || [],
              additional: {},
            };
          },
          cacheOn: ['type_id'],
          multi: false,
          label: t('pages.warehouse.products.unit'),
        }),
        Show(
          [
            Decimal('planned_price', t('pages.warehouse.products.price')),
            Text('article', t('pages.warehouse.products.article')),
          ],
          '',
          () =>
            obtainTypeId === ProductObtainType.PRODUCED ||
            obtainTypeId === ProductObtainType.RECYCLE_GAINED
        ),
        Checkbox('active', t('pages.warehouse.products.isActual')),
        TextArea('comment', t('pages.warehouse.products.comment')),
      ]),
    ]);

    setFooterElements([
      Button('save'),
      Link(
        route(WAREHOUSE_PRODUCTS_VIEW, { farmId, obtainTypeId, id }),
        'cancel'
      ),
    ]);
  }, [
    product,
    farmId,
    id,
    obtainTypeId,
    classifierService,
    t,
    setBodyElements,
    setFooterElements,
  ]);

  const submit = useCallback<SubmitType<any>>(
    (form) => {
      const updatedProduct: Product = {
        ...product,
        obtain_type_id: obtainTypeId,
        category_id: form.category_id.value,
        type_id: form.type_id.value,
        name: typeof form.name === 'string' ? form.name : form.name.label,
        unit: form.unit.value,
        planned_price: form.planned_price,
        article: form.article,
        active: form.active,
        comment: form.comment,
      };
      return [
        productService.update(farmId, id, updatedProduct),
        () =>
          history.push(
            route(WAREHOUSE_PRODUCTS_VIEW, { farmId, obtainTypeId, id })
          ),
      ];
    },
    [product, farmId, id, obtainTypeId, productService, history]
  );

  const formTitle = product
    ? typeof product.name === 'string'
      ? product.name
      : product.name.label
    : t('pages.warehouse.products.edit-product');

  return (
    <Form
      mode={'edit'}
      title={formTitle}
      backTo={null}
      body={bodyElements}
      footer={footerElements}
      values={product}
      submit={submit}
      schema={productSchema}
      noticeAccessor="EditForm"
    />
  );
}
